/**
 * Authentication API
 * 
 * Handles magic link authentication flow with real Soly API:
 * 1. Request magic link (POST /auth/magic-link)
 * 2. Verify token (POST /auth/verify)
 * 3. Logout (POST /auth/logoff)
 */

import { apiClient, ApiError } from './client'

/**
 * Organization item returned in verify response
 */
export interface OrganizationItem {
  organizationId: string
  organizationName: string | null
}

/**
 * Environment-level feature flags surfaced by the backend (via /auth/me, login, …).
 * Single source of truth lives in the backend config — the UI only reads it to decide
 * whether to show entry points for features that are still being built.
 */
export interface FeatureFlags {
  planning: boolean
}

/** Safe default: a feature is off unless the backend explicitly enables it. */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = { planning: false }

/**
 * User information from authentication
 */
export interface User {
  userId: string
  email: string
  displayName: string | null
  selectedOrganizationId: string | null
  organizations: OrganizationItem[] | null
  permissions: string[]
  features: FeatureFlags
  /**
   * Organization modules switched on by Soly (SaaS entitlements: « purchase.mailbox »…). Optional:
   * a session stored before modules were surfaced has none, which reads as « module off ».
   */
  modules?: string[]
}

/**
 * Request to create a magic link
 */
export interface CreateMagicLinkRequest {
  email: string
}

/**
 * Request to verify a magic link token
 */
export interface VerifyMagicLinkRequest {
  token: string
}

/**
 * Response from verifying magic link
 */
export interface VerifyMagicLinkResponse {
  userId: string
  email: string | null
  displayName: string | null
  selectedOrganizationId: string | null
  organizations: OrganizationItem[] | null
  permissions: string[]
  features?: FeatureFlags
  modules?: string[]
}

/**
 * Admin details for organization registration
 */
export interface AdminDto {
  email: string
  firstName: string
  lastName: string
  password: string
}

/**
 * Request to register a new organization with admin user
 */
export interface RegisterOrganizationRequest {
  organizationName: string
  admin: AdminDto
}

/**
 * Login with email and password
 * Backend: POST /auth/login
 */
export async function login(email: string, password: string): Promise<User> {
  const response = await apiClient.post<VerifyMagicLinkResponse>('/auth/login', {
    email,
    password,
  })
  return mapVerifyResponseToUser(response)
}

/**
 * Request password reset email
 * Backend: POST /auth/request-password-reset
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post<void>('/auth/request-password-reset', { email })
}

/**
 * Reset password with token and auto-login
 * Backend: POST /auth/reset-password
 */
export async function resetPassword(
  token: string,
  email: string,
  newPassword: string
): Promise<User> {
  const response = await apiClient.post<VerifyMagicLinkResponse>('/auth/reset-password', {
    token,
    email,
    newPassword,
  })
  return mapVerifyResponseToUser(response)
}

/**
 * Set or change password for authenticated user
 * Backend: POST /auth/set-password
 */
export async function setPassword(
  newPassword: string,
  currentPassword?: string
): Promise<void> {
  await apiClient.post<void>('/auth/set-password', {
    newPassword,
    currentPassword,
  })
}

/**
 * Register a new user
 * Backend: POST /organizations/register
 */
export async function register(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  organizationName?: string
): Promise<User> {
  const request: RegisterOrganizationRequest = {
    organizationName: organizationName || `${firstName}'s Organization`,
    admin: {
      email,
      firstName,
      lastName,
      password,
    },
  }
  const response = await apiClient.post<VerifyMagicLinkResponse>('/organizations/register', request)
  return mapVerifyResponseToUser(response)
}

/**
 * Request a magic link to be sent to the email
 * Backend: POST /auth/magic-link
 */
export async function requestMagicLink(email: string): Promise<void> {
  const request: CreateMagicLinkRequest = { email }
  await apiClient.post<void>('/auth/magic-link', request)
}

/**
 * Verify magic link token and establish session
 * Backend: POST /auth/verify
 */
export async function verifyMagicLink(token: string): Promise<VerifyMagicLinkResponse> {
  const request: VerifyMagicLinkRequest = { token }
  return apiClient.post<VerifyMagicLinkResponse>('/auth/verify', request)
}

/**
 * Get current authenticated user
 * Fetches from backend /auth/me endpoint and caches in localStorage
 */
const USER_STORAGE_KEY = 'soly_user'

export function setCurrentUser(user: User | null) {
  if (typeof window === 'undefined') return
  
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
  } else {
    localStorage.removeItem(USER_STORAGE_KEY)
  }
}

export async function getCurrentUser(
  options: { allowCachedFallback?: boolean } = {},
): Promise<User | null> {
  const { allowCachedFallback = true } = options
  if (typeof window === 'undefined') return null

  try {
    // Try to get user from server first (validates session).
    // `skipUnauthorizedHandler` is critical: this probe is the mount-time
    // auth check and is *expected* to 401 for anonymous visitors on
    // public routes (/login, /register, /verify, /reset-password).
    // Letting the global `onUnauthorized` handler fire here would kick
    // users off those pages on load — the bug that led to the handler
    // being commented out (commit f4aebc5).
    const response = await apiClient.get<VerifyMagicLinkResponse>('/auth/me', {
      skipUnauthorizedHandler: true,
      // The session refresh must never be able to trigger a session refresh (SOLY-REGIE-006).
      skipForbiddenHandler: true,
    })
    const user = mapVerifyResponseToUser(response)
    
    // Update localStorage cache
    setCurrentUser(user)
    
    return user
  } catch (error) {
    // If 401 or ApiError with status 401, session expired - clear cache
    if (error instanceof ApiError && error.status === 401) {
      setCurrentUser(null)
      return null
    }
    
    // The forbidden-refresh path opts OUT of the cache, and it has to.
    //
    // This function never rejects otherwise: on anything that is not a 401 it returns the user it
    // finds in localStorage. That made the `.catch` on the forbidden refresh dead code, and turned
    // one blip into the exact state the refresh exists to escape — a 403 arrives, /auth/me hits a
    // transient 502, and the STALE permission map (the revoked one) is written back into state
    // while the 30 s cooldown stays armed with no retry. Failing loudly here lets the caller reopen
    // its window and leave the user's permissions alone.
    //
    // It covers everything EXCEPT a 401, which is handled above and still resolves to null: there
    // the session really is gone, so logging the user out is the right answer. That path is
    // reachable in production - OrganizationAccessMiddleware answers 403 and signs the user out in
    // the same request, so the very next /auth/me is a 401 - and it reaches the correct outcome
    // through the branch this option does not touch.
    if (!allowCachedFallback) throw error

    // Network error or other issue - fallback to localStorage cache
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY)
      if (!stored) return null
      
      const user = JSON.parse(stored)
      console.warn('Using cached user data due to network error')
      return user
    } catch (parseError) {
      console.error('Failed to parse stored user:', parseError)
      localStorage.removeItem(USER_STORAGE_KEY)
      return null
    }
  }
}

/**
 * Logout current user
 * Backend: POST /auth/logoff
 */
export async function logout(): Promise<void> {
  await apiClient.post<void>('/auth/logoff')
  setCurrentUser(null)
}

/**
 * Check if user is authenticated
 * This is a client-side check - server validates on each request
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser()
  return user !== null
}

/**
 * Convert VerifyMagicLinkResponse to User format
 */
export function mapVerifyResponseToUser(response: VerifyMagicLinkResponse): User {
  return {
    userId: response.userId,
    email: response.email || '',
    displayName: response.displayName,
    selectedOrganizationId: response.selectedOrganizationId,
    organizations: response.organizations,
    permissions: Array.isArray(response.permissions) ? response.permissions : [],
    features: response.features ?? DEFAULT_FEATURE_FLAGS,
    modules: Array.isArray(response.modules) ? response.modules : [],
  }
}
